/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';

export const PUSH_PERMISSION_STATES = {
  UNSUPPORTED: 'unsupported',
  DEFAULT: 'default',
  GRANTED: 'granted',
  DENIED: 'denied',
};

export const PUSH_ERRORS = {
  UNSUPPORTED: 'unsupported',
  DENIED: 'denied',
  DISMISSED: 'dismissed',
  NOT_CONFIGURED: 'not_configured',
  SUBSCRIBE_FAILED: 'subscribe_failed',
};

export const isPushSupported = () =>
  !!window.isSecureContext &&
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

export const getPushPermissionState = () => {
  if (!isPushSupported()) return PUSH_PERMISSION_STATES.UNSUPPORTED;
  return Notification.permission;
};

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => callback(registration))
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  if (auth.hasAuthCookie()) {
    return NotificationSubscriptions.create(
      getPushSubscriptionPayload(subscription)
    );
  }
  return null;
};

export const registerSubscription = (
  onSuccess = () => {},
  onError = () => {},
  { rollbackOnFailure = false } = {}
) => {
  if (!window.chatwootConfig.vapidPublicKey) {
    onError(PUSH_ERRORS.NOT_CONFIGURED);
    return;
  }
  let browserSubscription = null;
  navigator.serviceWorker
    .register('/sw.js')
    .then(() => navigator.serviceWorker.ready)
    .then(serviceWorkerRegistration =>
      serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: window.chatwootConfig.vapidPublicKey,
      })
    )
    .then(subscription => {
      browserSubscription = subscription;
      return sendRegistrationToServer(subscription);
    })
    .then(() => {
      onSuccess();
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('Push subscription registration failed:', error);
      // When the user is enabling push, don't leave a browser subscription the
      // server doesn't know about, otherwise the toggle shows "on" while nothing
      // is delivered. Background re-syncs keep the subscription so a transient
      // server error doesn't silently switch push off.
      if (rollbackOnFailure && browserSubscription) {
        browserSubscription.unsubscribe();
      }
      onError(PUSH_ERRORS.SUBSCRIBE_FAILED);
    });
};

export const requestPushPermissions = ({
  onSuccess = () => {},
  onError = () => {},
}) => {
  if (!isPushSupported()) {
    // eslint-disable-next-line no-console
    console.warn('Push notifications are not supported');
    onError(PUSH_ERRORS.UNSUPPORTED);
  } else if (Notification.permission === 'granted') {
    registerSubscription(onSuccess, onError, { rollbackOnFailure: true });
  } else if (Notification.permission === 'denied') {
    onError(PUSH_ERRORS.DENIED);
  } else {
    Notification.requestPermission(permission => {
      if (permission === 'granted') {
        registerSubscription(onSuccess, onError, { rollbackOnFailure: true });
      } else if (permission === 'denied') {
        onError(PUSH_ERRORS.DENIED);
      } else {
        onError(PUSH_ERRORS.DISMISSED);
      }
    });
  }
};
